import { useCallback, useEffect, useRef, useState } from "react";

import {
  RunItemStatus,
  RunKind,
  StoredRun,
  isRunStoreKey,
  loadRuns,
  saveRuns,
} from "./runStore";

/**
 * Generic run-history hook for a Catalog Manager workflow `kind`. Persists runs
 * to the (swappable) runStore, keeps multiple tabs in sync, and exposes simple
 * upsert / patch / item-status / remove helpers. It does NOT poll — the two
 * flows using it (grocery replacement, sourcing finder) await their chain runs
 * inline and persist the finished result; the ingest flow keeps its own
 * background poller (useIngestJobs).
 */
export const useRunHistory = <TPayload = unknown>(kind: RunKind) => {
  const [runs, setRuns] = useState<StoredRun<TPayload>[]>(() =>
    loadRuns<TPayload>(kind)
  );
  const runsRef = useRef<StoredRun<TPayload>[]>(runs);
  runsRef.current = runs;

  const mutate = useCallback(
    (fn: (prev: StoredRun<TPayload>[]) => StoredRun<TPayload>[]) => {
      setRuns((prev) => {
        const next = fn(prev);
        runsRef.current = next;
        saveRuns(kind, next);
        return next;
      });
    },
    [kind]
  );

  // Insert or replace a run (by id). New runs go to the front.
  const saveRun = useCallback(
    (run: StoredRun<TPayload>) => {
      mutate((prev) => {
        const withTs = { ...run, updatedAt: Date.now() };
        const idx = prev.findIndex((r) => r.id === run.id);
        if (idx === -1) return [withTs, ...prev];
        const next = [...prev];
        next[idx] = { ...next[idx], ...withTs };
        return next;
      });
    },
    [mutate]
  );

  const patchRun = useCallback(
    (id: string, patch: Partial<StoredRun<TPayload>>) =>
      mutate((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, ...patch, updatedAt: Date.now() } : r
        )
      ),
    [mutate]
  );

  // Record the action state for one item within a run (e.g. "added"/"written").
  const setItemStatus = useCallback(
    (id: string, itemKey: string, status: Omit<RunItemStatus, "at">) =>
      mutate((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const itemStatus = {
            ...(r.itemStatus || {}),
            [itemKey]: { ...status, at: Date.now() },
          };
          return { ...r, itemStatus, updatedAt: Date.now() };
        })
      ),
    [mutate]
  );

  const removeRun = useCallback(
    (id: string) => mutate((prev) => prev.filter((r) => r.id !== id)),
    [mutate]
  );

  const clearFinished = useCallback(
    () => mutate((prev) => prev.filter((r) => r.status === "running")),
    [mutate]
  );

  const clearAll = useCallback(() => mutate(() => []), [mutate]);

  const getRun = useCallback(
    (id: string): StoredRun<TPayload> | undefined =>
      runsRef.current.find((r) => r.id === id),
    []
  );

  // Keep other open tabs roughly in sync when they write runs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (isRunStoreKey(e.key)) {
        const fresh = loadRuns<TPayload>(kind);
        runsRef.current = fresh;
        setRuns(fresh);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [kind]);

  return {
    runs,
    saveRun,
    patchRun,
    setItemStatus,
    removeRun,
    clearFinished,
    clearAll,
    getRun,
  };
};

export type RunHistoryApi = ReturnType<typeof useRunHistory>;
